import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Sparkles, TrendingUp, DollarSign, Users, Scissors, ChevronsRight, Maximize2, Bookmark, MapPin, Clock, Briefcase, ChevronLeft, ChevronRight, ChevronUp, X, Layers, Building2, Info, ArrowUpRight, Star, CheckCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopHeaderBar from '../components/TopHeaderBar';
import ApplicationStartModal from '../components/ApplicationStartModal';
import { jobListings as mockJobs } from '../mockData';
import * as api from '../lib/api';

/* ═══════════ Filter Config (matching Duty AI) ═══════════ */
const FILTER_CATEGORIES = [
  {
    id: 'payRate', label: 'Pay Rate', icon: DollarSign,
    options: [
      { label: 'Any', value: 'any' },
      { label: '$0 - $50/hr', value: '0-50' },
      { label: '$50 - $100/hr', value: '50-100' },
      { label: '$100 - $150/hr', value: '100-150' },
      { label: '$150+/hr', value: '150+' },
    ],
  },
  {
    id: 'location', label: 'Location', icon: MapPin,
    options: [
      { label: 'Any', value: 'any' },
      { label: 'Remote', value: 'Remote' },
      { label: 'Hybrid', value: 'Hybrid' },
      { label: 'On-site', value: 'On-site' },
    ],
  },
  {
    id: 'commitment', label: 'Commitment', icon: Clock,
    options: [
      { label: 'Any', value: 'any' },
      { label: 'Full-time', value: 'Full-time' },
      { label: 'Part-time', value: 'Part-time' },
      { label: 'Contract', value: 'Contract' },
    ],
  },
  {
    id: 'domain', label: 'Domain', icon: Layers,
    options: [
      { label: 'Any', value: 'any' },
      { label: 'Software Engineering', value: 'Software Engineering' },
      { label: 'AI & Machine Learning', value: 'AI & Machine Learning' },
      { label: 'Data Science', value: 'Data Science' },
      { label: 'Research & Science', value: 'Research & Science' },
      { label: 'Healthcare', value: 'Healthcare' },
      { label: 'Consulting', value: 'Consulting' },
      { label: 'Operations', value: 'Operations' },
      { label: 'Media & Content', value: 'Media & Content' },
    ],
  },
  {
    id: 'referralAmount', label: 'Referral Amount', icon: Scissors,
    options: [
      { label: 'Any', value: 'any' },
      { label: '$100+', value: '100' },
      { label: '$300+', value: '300' },
      { label: '$500+', value: '500' },
    ],
  },
  {
    id: 'workArrangement', label: 'Work Arrangement', icon: Building2,
    options: [
      { label: 'Any', value: 'any' },
      { label: 'Hourly contract', value: 'hourly' },
      { label: 'Fixed-price', value: 'fixed' },
    ],
  },
];

/* ═══════════ Filter Dropdown Component ═══════════ */
const FilterDropdown = ({ open, onClose, activeFilters, setActiveFilters, anchorRef }) => {
  const [expandedFilter, setExpandedFilter] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
        setExpandedFilter(null);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const handleSelectOption = (filterId, value) => {
    setActiveFilters(prev => {
      if (value === 'any') {
        const copy = { ...prev };
        delete copy[filterId];
        return copy;
      }
      return { ...prev, [filterId]: value };
    });
    setExpandedFilter(null);
    onClose();
  };

  return (
    <div ref={dropdownRef} className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 min-w-[220px]" data-testid="filter-dropdown">
      {expandedFilter === null ? (
        // Main menu
        FILTER_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const hasActive = activeFilters[cat.id] && activeFilters[cat.id] !== 'any';
          return (
            <button
              key={cat.id}
              data-testid={`filter-menu-${cat.id}`}
              onClick={() => setExpandedFilter(cat.id)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon className={`w-4 h-4 ${hasActive ? 'text-[#0A3900]' : 'text-gray-400'}`} />
              <span className={hasActive ? 'text-[#0A3900] font-medium' : ''}>{cat.label}</span>
              {hasActive && (
                <span className="ml-auto w-2 h-2 rounded-full bg-[#0A3900]" />
              )}
            </button>
          );
        })
      ) : (
        // Sub-menu with options
        <>
          <button
            onClick={() => setExpandedFilter(null)}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs text-gray-400 hover:text-gray-600 border-b border-gray-100 mb-1"
          >
            <ChevronLeft className="w-3 h-3" />
            {FILTER_CATEGORIES.find(c => c.id === expandedFilter)?.label}
          </button>
          {FILTER_CATEGORIES.find(c => c.id === expandedFilter)?.options.map((opt) => {
            const isSelected = (activeFilters[expandedFilter] || 'any') === opt.value;
            return (
              <button
                key={opt.value}
                data-testid={`filter-option-${expandedFilter}-${opt.value}`}
                onClick={() => handleSelectOption(expandedFilter, opt.value)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isSelected ? 'text-[#0A3900] bg-[#F1F5E6] font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                isSelected ? 'border-[#0A3900]' : 'border-gray-300'
              }`}>
                {isSelected && <div className="w-2 h-2 rounded-full bg-[#0A3900]" />}
              </div>
                {opt.label}
              </button>
            );
          })}
        </>
      )}
    </div>
  );
};

/* ═══════════ Active Filter Pills ═══════════ */
const FilterPills = ({ activeFilters, setActiveFilters }) => {
  const pills = Object.entries(activeFilters).filter(([, v]) => v !== 'any');
  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mb-4" data-testid="active-filter-pills">
      {pills.map(([filterId, value]) => {
        const cat = FILTER_CATEGORIES.find(c => c.id === filterId);
        const opt = cat?.options.find(o => o.value === value);
        return (
          <span key={filterId} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F1F5E6] text-[#0A3900] rounded-full text-xs font-medium">
            {opt?.label || value}
            <X
              className="w-3 h-3 cursor-pointer hover:text-[#0A3900]"
              onClick={() => setActiveFilters(prev => { const copy = { ...prev }; delete copy[filterId]; return copy; })}
            />
          </span>
        );
      })}
      <button
        data-testid="clear-all-filters"
        onClick={() => setActiveFilters({})}
        className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1"
      >
        Clear all
      </button>
    </div>
  );
};

/* ═══════════ MAIN PAGE ═══════════ */
const ExplorePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSort, setActiveSort] = useState('best-match');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(searchParams.get('listingId') || null);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const filterBtnRef = useRef(null);
  const perPage = 20;

  useEffect(() => { loadJobs(); }, []);

  useEffect(() => {
    const id = searchParams.get('listingId');
    if (id) setSelectedJobId(id);
  }, [searchParams]);

  const inferCategory = useCallback((job) => {
    const t = (job.title || '').toLowerCase();
    if (t.includes('software') || t.includes('swe') || t.includes('engineer') || t.includes('frontend') || t.includes('backend') || t.includes('operating system')) return 'Software Engineering';
    if (t.includes('machine learning') || t.includes('ml ') || t.includes('red-team') || t.includes('adversarial')) return 'AI & Machine Learning';
    if (t.includes('data scientist') || t.includes('data science')) return 'Data Science';
    if (t.includes('math') || t.includes('phd') || t.includes('research') || t.includes('scientific') || t.includes('stem')) return 'Research & Science';
    if (t.includes('radiolog') || t.includes('cardiolog') || t.includes('medical')) return 'Healthcare';
    if (t.includes('consult') || t.includes('management')) return 'Consulting';
    if (t.includes('procurement') || t.includes('operations')) return 'Operations';
    if (t.includes('listening') || t.includes('media') || t.includes('content')) return 'Media & Content';
    return 'Software Engineering';
  }, []);

  /* ═══════════ Badge assignment sets ═══════════ */
  const TALENT_NETWORK_IDS = useMemo(() => new Set(['1', '4', '8', '11', '16']), []);
  const TOP_APPLICANT_IDS = useMemo(() => new Set(['1', '8', '16']), []);
  const ONE_CLICK_APPLY_IDS = useMemo(() => new Set(['2', '5', '11', '13']), []);

  const normalizeJob = useCallback((job) => {
    const id = job.id || job.job_id;
    return {
      ...job,
      id,
      type: job.type || job.job_type || 'Full-time',
      hiredCount: job.hiredCount || job.applications_count || 0,
      category: job.category || inferCategory(job),
      referralBonus: job.referralBonus || ((job.payRange?.max || job.pay_range?.max || 50) * 4),
      avatars: job.avatars || [
        `https://i.pravatar.cc/150?img=${(parseInt(id, 36) || 1) % 70}`,
        `https://i.pravatar.cc/150?img=${((parseInt(id, 36) || 2) + 10) % 70}`,
        `https://i.pravatar.cc/150?img=${((parseInt(id, 36) || 3) + 20) % 70}`
      ],
      isTalentNetwork: TALENT_NETWORK_IDS.has(String(id)),
      isTopApplicant: TOP_APPLICANT_IDS.has(String(id)),
      oneClickApply: ONE_CLICK_APPLY_IDS.has(String(id)),
    };
  }, [inferCategory, TALENT_NETWORK_IDS, TOP_APPLICANT_IDS, ONE_CLICK_APPLY_IDS]);

  const loadJobs = async () => {
    try {
      const data = await api.getJobs();
      const list = Array.isArray(data) ? data : (data.jobs || []);
      const apiNormalized = list.map(normalizeJob);
      const mockNormalized = mockJobs.map(normalizeJob);
      // Merge API jobs with mock jobs (deduplicate by title)
      const apiTitles = new Set(apiNormalized.map(j => j.title));
      const extra = mockNormalized.filter(j => !apiTitles.has(j.title));
      setJobs([...apiNormalized, ...extra]);
    } catch {
      setJobs(mockJobs.map(normalizeJob));
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      if (searchTerm && !j.title?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      const pay = j.payRange || j.pay_range || { min: 0, max: 0 };

      // Pay Rate filter
      if (activeFilters.payRate) {
        const pr = activeFilters.payRate;
        if (pr === '0-50' && pay.min > 50) return false;
        if (pr === '50-100' && (pay.max < 50 || pay.min > 100)) return false;
        if (pr === '100-150' && (pay.max < 100 || pay.min > 150)) return false;
        if (pr === '150+' && pay.max < 150) return false;
      }
      // Location filter
      if (activeFilters.location && j.location !== activeFilters.location) return false;
      // Commitment filter
      if (activeFilters.commitment && j.type !== activeFilters.commitment) return false;
      // Domain filter
      if (activeFilters.domain && j.category !== activeFilters.domain) return false;
      // Referral Amount filter
      if (activeFilters.referralAmount) {
        const minRef = parseInt(activeFilters.referralAmount);
        if ((j.referralBonus || 0) < minRef) return false;
      }
      return true;
    });
  }, [jobs, searchTerm, activeFilters]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (activeSort === 'most-pay') return (b.payRange?.max || b.pay_range?.max || 0) - (a.payRange?.max || a.pay_range?.max || 0);
      if (activeSort === 'trending') return (b.hiredCount || 0) - (a.hiredCount || 0);
      return 0;
    });
  }, [filtered, activeSort]);

  const totalPages = Math.ceil(sorted.length / perPage);
  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  const selectedJob = useMemo(() => {
    if (!selectedJobId) return null;
    return jobs.find(j => j.id === selectedJobId || j.job_id === selectedJobId);
  }, [selectedJobId, jobs]);

  const handleSelectJob = (job) => {
    const id = job.id || job.job_id;
    if (selectedJobId === id) {
      setSelectedJobId(null);
      setSearchParams({});
    } else {
      setSelectedJobId(id);
      setSearchParams({ listingId: id });
    }
  };

  const handleClosePanel = () => {
    setSelectedJobId(null);
    setSearchParams({});
  };

  const getPayRange = (j) => j.payRange || j.pay_range || { min: 0, max: 0 };
  const panelOpen = !!selectedJob;
  const activeFilterCount = Object.keys(activeFilters).filter(k => activeFilters[k] !== 'any').length;

  // Application Start Modal state
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const [applicationModalJob, setApplicationModalJob] = useState(null);

  const handleOpenApplicationModal = (job) => {
    setApplicationModalJob(job);
    setApplicationModalOpen(true);
  };

  const handleCloseApplicationModal = () => {
    setApplicationModalOpen(false);
    setApplicationModalJob(null);
  };

  const handleStartApplication = (signupData) => {
    handleCloseApplicationModal();
    const jobId = applicationModalJob?.id || applicationModalJob?.job_id;
    navigate(`/apply/${jobId}`);
  };

  return (
    <div className="flex min-h-screen bg-white" data-testid="explore-page">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[60px] flex flex-col pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <TopHeaderBar />
        <div className="flex-1 flex relative">
        {/* Left: Job List - hidden on mobile when panel is open */}
        <div className={`flex-1 min-w-0 transition-all duration-300 ${panelOpen ? 'hidden md:block md:max-w-[40%]' : ''}`}>
          <div className={`${panelOpen ? 'px-4 md:px-6' : 'px-4 sm:px-6 md:px-10 lg:px-[120px]'} pt-6 md:pt-8 pb-4 transition-all duration-300`}>
            <h1 className="text-[22px] sm:text-[28px] font-semibold mb-4 md:mb-6" data-testid="explore-title" style={{ color: '#1a1a1a' }}>Explore opportunities</h1>

            {/* Search + Filter Row */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
              {/* Filter icon + dropdown */}
              <div className="relative" ref={filterBtnRef}>
                <button
                  data-testid="filter-toggle-btn"
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors shrink-0 ${
                    filterOpen || activeFilterCount > 0
                      ? 'text-[#0A3900] bg-[#F1F5E6]'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 1h18M3 7h12M6 13h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0A3900] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <FilterDropdown
                  open={filterOpen}
                  onClose={() => setFilterOpen(false)}
                  activeFilters={activeFilters}
                  setActiveFilters={(v) => { setActiveFilters(v); setPage(1); }}
                  anchorRef={filterBtnRef}
                />
              </div>
              {/* Search */}
              <div className="relative flex-1 min-w-[120px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  data-testid="search-input"
                  type="text"
                  placeholder="Type to search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="w-full h-9 pl-10 pr-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 transition-colors"
                />
              </div>
              {/* Sort + Refer - right side like Duty AI */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="relative">
                  <button
                    data-testid="sort-dropdown-btn"
                    onClick={() => {
                      const el = document.getElementById('sort-dropdown');
                      if (el) el.classList.toggle('hidden');
                    }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-gray-500" />
                    {activeSort === 'best-match' ? 'Priority' : activeSort === 'trending' ? 'Trending' : activeSort === 'most-pay' ? 'Most pay' : 'Priority'}
                    <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" /></svg>
                  </button>
                  <div id="sort-dropdown" className="hidden absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                    {[
                      { id: 'best-match', label: 'Best match' },
                      { id: 'priority', label: 'Priority' },
                      { id: 'trending', label: 'Trending' },
                      { id: 'most-pay', label: 'Most pay' },
                    ].map(s => (
                      <button
                        key={s.id}
                        data-testid={`sort-${s.id}-btn`}
                        onClick={() => { setActiveSort(s.id); document.getElementById('sort-dropdown')?.classList.add('hidden'); }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${activeSort === s.id ? 'text-[#0A3900] bg-[#F1F5E6] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  data-testid="refer-earn-btn"
                  onClick={() => navigate('/referrals')}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
                  style={{ backgroundColor: '#6C5CE7' }}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Refer & earn</span>
                  <span className="sm:hidden">Refer</span>
                </button>
              </div>
            </div>

            {/* Active Filter Pills */}
            <FilterPills activeFilters={activeFilters} setActiveFilters={(v) => { setActiveFilters(v); setPage(1); }} />
          </div>

          {/* Job List */}
          <div className={`${panelOpen ? 'px-4 md:px-6' : 'px-4 sm:px-6 md:px-10 lg:px-[120px]'} pb-6 overflow-y-auto transition-all duration-300`} style={{ maxHeight: 'calc(100vh - 220px)' }}>
            {loading ? (
              <div className="text-center py-16 text-gray-400">Loading...</div>
            ) : sorted.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg mb-2">No opportunities match your filters</p>
                <p className="text-gray-400 text-sm mb-4">Try adjusting your search or filters</p>
                <button onClick={() => { setActiveFilters({}); setSearchTerm(''); }} className="text-sm text-[#0A3900] hover:text-[#1A5C0A] font-medium">Clear all filters</button>
              </div>
            ) : (
              <>
                <div className={`grid ${panelOpen ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-5`} data-testid="job-grid">
                  {paged.map((job) => {
                    const pay = getPayRange(job);
                    const jobId = job.id || job.job_id;
                    const isSelected = selectedJobId === jobId;
                    return (
                      <div
                        key={jobId}
                        data-testid={`job-card-${jobId}`}
                        onClick={() => handleSelectJob(job)}
                        className={`group flex flex-col justify-between rounded-lg border cursor-pointer transition-all hover:border-gray-400 hover:shadow-sm ${
                          isSelected
                            ? 'border-[#6C5CE7] border-2 bg-white'
                            : 'border-gray-200 bg-white'
                        }`}
                        style={{ minHeight: '170px' }}
                      >
                        <div className="px-5 pt-5">
                          {/* Row 1: Title + 1-click apply badge (same row) + hover arrow */}
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900 text-[15px] leading-snug line-clamp-1 min-w-0 flex-1 group-hover:font-bold transition-all duration-150">
                              {job.title}
                            </h3>
                            {/* 1-click apply badge - inline with title */}
                            {job.oneClickApply && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 whitespace-nowrap shrink-0">
                                <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                1-click apply
                              </span>
                            )}
                            {/* Apply arrow - visible on hover */}
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0">
                              <ArrowUpRight className="w-4 h-4 text-gray-600" />
                            </div>
                          </div>

                          {/* Row 2: Talent network badge (if applicable) */}
                          {job.isTalentNetwork && (
                            <div className="mb-1.5">
                              <span className="inline-block px-2.5 py-0.5 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-full">
                                Talent network
                              </span>
                            </div>
                          )}

                          {/* Row 3: Top applicant badge (if applicable) */}
                          {job.isTopApplicant && (
                            <div className="flex items-center gap-1 mb-1">
                              <Star className="w-3.5 h-3.5 text-[#6C5CE7] fill-[#6C5CE7]" />
                              <span className="text-[12px] font-medium text-[#6C5CE7]">Top applicant</span>
                            </div>
                          )}

                          {/* Pay range - only show if NOT a talent network job (matching mercor) */}
                          {!job.isTalentNetwork && (
                            <p className="text-gray-500 text-[14px]">{pay.min === pay.max ? `$${pay.min}` : `$${pay.min} - $${pay.max}`} / hour</p>
                          )}
                        </div>
                        {/* Bottom: avatars + hired + referral */}
                        <div className="flex items-center justify-between px-5 pb-4 gap-2">
                          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                            {(job.avatars || []).length > 0 && (job.hiredCount > 0) && (
                              <>
                                <div className="flex -space-x-1.5 shrink-0">
                                  {(job.avatars || []).slice(0, 3).map((av, i) => (
                                    <img key={i} src={av} alt="" className="w-[26px] h-[26px] rounded-full border-2 border-white object-cover" />
                                  ))}
                                </div>
                                <span className="text-[12px] sm:text-[13px] text-gray-400 whitespace-nowrap truncate">{job.hiredCount} hired this month</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-[12px] sm:text-[13px] text-gray-400 shrink-0">
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" /></svg>
                            <span>${job.referralBonus || 0}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-1 mt-6 pb-4" data-testid="pagination">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>

                    {/* Page numbers with ellipsis */}
                    {(() => {
                      const pages = [];
                      const showPages = new Set();
                      // Always show first 4 pages
                      for (let i = 1; i <= Math.min(4, totalPages); i++) showPages.add(i);
                      // Always show last 2 pages
                      for (let i = Math.max(1, totalPages - 1); i <= totalPages; i++) showPages.add(i);
                      // Current page neighbors
                      showPages.add(page);
                      if (page > 1) showPages.add(page - 1);
                      if (page < totalPages) showPages.add(page + 1);

                      const sortedPages = [...showPages].sort((a, b) => a - b);
                      let last = 0;
                      sortedPages.forEach((p) => {
                        if (p - last > 1) {
                          pages.push(<span key={`ellipsis-${p}`} className="px-1 text-gray-400 text-sm">···</span>);
                        }
                        pages.push(
                          <button
                            key={p}
                            data-testid={`page-${p}-btn`}
                            onClick={() => setPage(p)}
                            className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                              page === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {p}
                          </button>
                        );
                        last = p;
                      });
                      return pages;
                    })()}

                    <button
                      disabled={page === totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right: Job Detail Panel */}
        {panelOpen && selectedJob && (
          <DetailPanel job={selectedJob} onClose={handleClosePanel} getPayRange={getPayRange} onStartApplication={handleOpenApplicationModal} />
        )}
      </div>
      </div>

      {/* Application Start Modal */}
      <ApplicationStartModal
        isOpen={applicationModalOpen}
        onClose={handleCloseApplicationModal}
        job={applicationModalJob}
        onStartApplication={handleStartApplication}
      />
    </div>
  );
};

/* ═══════════ Job Detail Panel (right side) ═══════════ */
const DetailPanel = ({ job, onClose, getPayRange, onStartApplication }) => {
  const pay = getPayRange(job);
  const navigate = useNavigate();

  const applicationSteps = [
    { label: 'Resume', status: 'Not done', core: false },
    { label: 'General Interview', status: 'Not done', core: true },
    { label: 'Availability', status: 'Not done', core: false },
    { label: 'Work Authorization', status: 'Not done', core: false },
  ];

  return (
    <div data-testid="job-detail-panel" className="fixed inset-0 md:relative md:inset-auto w-full md:w-[55%] lg:w-[55%] border-l border-gray-200 bg-white flex flex-col h-screen md:sticky top-0 z-50 md:z-auto">
      {/* Panel Header */}
      <div className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-gray-100">
        <button data-testid="close-detail-panel" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex items-center gap-1" title="Close panel">
          <ChevronsRight className="w-5 h-5 hidden md:block" />
          <svg className="w-5 h-5 md:hidden" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
          <span className="text-sm md:hidden">Back</span>
        </button>
        <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Expand">
          <Maximize2 className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Save">
          <Bookmark className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {/* Title + Pay */}
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 pr-4" data-testid="detail-job-title">{job.title}</h2>
          <div className="text-right shrink-0">
            <p className="text-2xl font-bold text-gray-900">
              {pay.min === pay.max ? `$${pay.min}` : `$${pay.min}-$${pay.max}`}
            </p>
            <p className="text-sm text-gray-500">per hour</p>
          </div>
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-3 mb-6 flex-wrap text-sm text-gray-600">
          <span className="inline-flex items-center gap-1.5 text-gray-500">
            <Briefcase className="w-4 h-4 text-gray-400" />
            Hourly contract
          </span>
          <span className="inline-flex items-center gap-1.5 text-gray-500">
            <MapPin className="w-4 h-4 text-gray-400" />
            {job.location || 'Remote'}
          </span>
          {(job.avatars || []).length > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1.5">
                {(job.avatars || []).slice(0, 3).map((av, i) => (
                  <img key={i} src={av} alt="" className="w-6 h-6 rounded-full border-2 border-white object-cover" />
                ))}
              </div>
              <span className="text-gray-500">{job.hiredCount || 0} hired this month</span>
            </div>
          )}
        </div>

        {/* Company */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0A3900] flex items-center justify-center text-white font-bold text-lg shrink-0">D</div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Posted by Duty AI</p>
              <p className="text-xs text-gray-500">dutyai.com</p>
            </div>
          </div>
          <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Save">
            <Bookmark className="w-5 h-5" />
          </button>
        </div>

        {/* Application Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">Application</h3>
              <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-testid="application-status">Not started</span>
            </div>
            <ChevronUp className="w-5 h-5 text-gray-400" />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
            <span>0 of {applicationSteps.length} steps completed</span>
            <span>0%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-100 rounded-full mb-5">
            <div className="h-full bg-[#0A3900] rounded-full transition-all" style={{ width: '0%' }} />
          </div>

          <div className="space-y-0">
            {applicationSteps.map((step, i) => (
              <div key={i} className="flex items-center justify-between py-3.5 border-b border-gray-100 last:border-0">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{step.label}</span>
                    {step.core && (
                      <span className="px-1.5 py-0.5 bg-gray-100 text-[10px] font-semibold text-gray-500 rounded uppercase tracking-wide">Core</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{step.status}</span>
                </div>
                <div className="w-5 h-5 rounded-full border-2 border-gray-200 shrink-0" />
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-4 flex items-start gap-1.5">
            <span className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center text-[9px] font-bold text-gray-400 shrink-0 mt-0.5">i</span>
            All application steps are reused whenever another role requires the same step, so you never have to upload your resume or take the same interview twice.
          </p>
        </div>

        {/* Job Description */}
        <div className="mb-6 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-700 leading-relaxed">{job.description || 'No description available.'}</p>
          {(job.requirements || []).length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Requirements</h4>
              <ul className="list-disc pl-5 space-y-1">
                {job.requirements.map((req, i) => (<li key={i} className="text-sm text-gray-600">{req}</li>))}
              </ul>
            </div>
          )}
          {(job.skills || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {job.skills.map((skill, i) => (<span key={i} className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">{skill}</span>))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="px-6 py-4 border-t border-gray-100">
        <button
          data-testid="start-application-btn"
          onClick={() => onStartApplication(job)}
          className="w-full py-3 text-white font-semibold rounded-xl transition-opacity hover:opacity-90 text-sm"
          style={{ backgroundColor: '#082900' }}
        >
          Start Application
        </button>
        <p className="text-xs text-center text-gray-400 mt-2">Click to begin your application</p>
      </div>
    </div>
  );
};

export default ExplorePage;
